'use client';

import { Picture, Tile } from '@petsmart-ui/sparky';
import { usePathname } from 'next/navigation';
import { useLayoutEffect, useState } from 'react';

import {
    getPromoOrigin,
    trackContentfulClick,
    trackingEvents,
    trackProductListFilter,
    trackTile6UpClick,
    trackVisualNavigationClicks,
} from '@libs/analytics';
import { BREAKPOINTS, clsx } from '@libs/common-next';

import { useBuildContext } from '../../../hooks';
import type { TilesProps } from '../types';
import { HorizontalCarousel } from './HorizontalCarousel';
import type { SixTileCarouselProps } from './types';
import './SixTileCarousel.scss';

export type FacetHitProps = {
    value: string;
    count: number;
};

export type DisjunctiveFacetsProps = {
    [key: string]: FacetHitProps[];
};

export default function SixTileCarousel({
    header,
    alignText = 'center',
    tiles,
    className,
    variant,
    overrideDefaultClass,
    breakpoints = {
        960: {
            perView: 3.5,
            gap: 16,
        },
        560: {
            perView: 2.5,
            gap: 8,
        },
    },
    ...other
}: SixTileCarouselProps) {
    const pathname = usePathname();
    const buildContext = useBuildContext();
    const [disjunctiveFacets, setDisjunctiveFacets] = useState<DisjunctiveFacetsProps>({});
    const [isDragging, setIsDragging] = useState(false);

    const getSelectedPetResults = (name: string) => {
        let results = 0;
        if (variant && disjunctiveFacets[variant]) {
            disjunctiveFacets[variant].map((facet: FacetHitProps) => {
                if (facet?.value === name && facet?.count) {
                    results = facet.count;
                }
                return results;
            });
        }
        return results;
    };

    const perViewDefault = tiles.length < 6 ? tiles.length : 6;
    const classNames = clsx('six-tile-carousel', className);

    const tileClick = (tile: TilesProps, index: number, clickedHeader: string | undefined) => {
        if (!tile?.link?.webUrl || isDragging) {
            return;
        }

        trackContentfulClick({
            position: index + 1,
            linkText: tile.title,
            linkUrl: tile.link.webUrl,
            promoTitle: clickedHeader ?? '',
            promoVariant: `Tile 6up - ${variant}`,
            promoOrigin: pathname ?? '',
            promoDesc: tile.internalTitle ?? '',
            promoType: `Tile 6up - ${variant}`,
        });

        if (variant === 'VisualNav') {
            if (getPromoOrigin(pathname) !== 'homepage') {
                trackProductListFilter({
                    position: index + 1,
                    linkUrl: tile.link.webUrl,
                    linkText: tile.title,
                    promoTitle: clickedHeader ?? '',
                    promoVariant: variant,
                    filterSelection: tile.title,
                    filterType: trackingEvents[variant].filter_type,
                    results: getSelectedPetResults(tile.title),
                });
            }

            trackVisualNavigationClicks({
                categoryId: buildContext.pageId ?? '',
                navPos: index + 1,
                text: tile.title,
            });
        }
    };

    useLayoutEffect(() => {
        const handleFacets = (data: Event) => {
            const facets: CustomEvent = data as CustomEvent;
            const disjunctiveFacetsObj: DisjunctiveFacetsProps = {};

            if (variant && facets?.detail.variant === variant) {
                disjunctiveFacetsObj[variant] = facets?.detail.hits;

                setDisjunctiveFacets(prevDisjunctiveFacets =>
                    Object.assign(prevDisjunctiveFacets, disjunctiveFacetsObj)
                );
            }
        };

        window.addEventListener('algoliaDisjunctiveFacets', data => {
            handleFacets(data);
        });

        return () => {
            window.removeEventListener('algoliaDisjunctiveFacets', handleFacets);
        };
    }, [variant, disjunctiveFacets]);

    const onArrowClick = (navigationClick: string) => {
        if (variant) {
            trackTile6UpClick({
                variant,
                name: header ?? '',
                position: null,
                navigationClick,
            });
        }
    };

    return (
        <div className="six-tile-carousel-container" data-testid="six-tile-carousel" {...other}>
            <HorizontalCarousel
                className={overrideDefaultClass && className ? className : classNames}
                perViewDefault={perViewDefault}
                onLeftArrowClick={() => onArrowClick('left')}
                onRightArrowClick={() => onArrowClick('right')}
                breakpoints={breakpoints}
                onDragging={isDrag => setIsDragging(isDrag)}
            >
                {tiles.map<React.ReactElement>((tile, index) => (
                    <div
                        key={tile.sysId || `${tile.title}-${index}-${tile.image?.remoteDesktopImageUrl || ''}`}
                        className={tiles.length < 6 ? 'carousel-item xl-static' : 'carousel-item'}
                    >
                        <Tile
                            data-testid="six-tile-carousel-tile"
                            title={tile.title}
                            alignText={alignText}
                            href={tile.link && !isDragging ? tile.link.webUrl : undefined}
                            className="six-tile-item"
                            onClick={() => {
                                tileClick(tile, index, header);

                                if (variant) {
                                    trackTile6UpClick({
                                        variant,
                                        name: tile.title,
                                        position: index + 1,
                                        navigationClick: null,
                                    });
                                }
                            }}
                        >
                            <Picture
                                imgSrc={tile.image.remoteDesktopImageUrl}
                                items={[
                                    {
                                        media: `(max-width: ${BREAKPOINTS.md})`,
                                        srcSet: tile.image.remoteMobileImageUrl ?? tile.image.remoteDesktopImageUrl,
                                    },
                                    {
                                        media: `(min-width: ${BREAKPOINTS.md})`,
                                        srcSet: tile.image.remoteDesktopImageUrl,
                                    },
                                ]}
                                imgAlt={tile.image.webAltText}
                            />
                        </Tile>
                    </div>
                ))}
            </HorizontalCarousel>
        </div>
    );
}
